import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faUsers, 
  faEdit, 
  faTrash, 
  faSpinner, 
  faTimes, 
  faSave, 
  faSearch,
  faLock,
  faEnvelope,
  faCheckCircle,
  faUserShield
} from '@fortawesome/free-solid-svg-icons';
import { AuthContext } from '../../context/AuthContext';

interface User {
  id: number;
  name: string;
  email: string;
  role: string;
  created_at: string;
}

const UserManagement: React.FC = () => {
  const { user: currentUser } = useContext(AuthContext);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  
  // User edit state
  const [editingUser, setEditingUser] = useState<number | null>(null);
  const [editFormData, setEditFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: ''
  });
  const [editLoading, setEditLoading] = useState(false);
  const [editSuccess, setEditSuccess] = useState<string | null>(null);
  const [editError, setEditError] = useState<string | null>(null);
  
  // Delete state
  const [deleteConfirm, setDeleteConfirm] = useState<number | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  
  useEffect(() => {
    fetchUsers();
  }, []);
  
  const fetchUsers = async () => {
    try {
      setLoading(true);
      const res = await axios.get('/api/auth/users');
      setUsers(res.data.users);
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error fetching users');
    } finally {
      setLoading(false);
    }
  };
  
  const handleEditClick = (user: User) => {
    setEditingUser(user.id);
    setEditFormData({
      name: user.name,
      email: user.email,
      password: '',
      role: user.role
    });
    setEditError(null);
    setEditSuccess(null);
  };
  
  const handleEditChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setEditFormData({
      ...editFormData,
      [e.target.name]: e.target.value
    });
    setEditError(null);
    setEditSuccess(null);
  };
  
  const handleEditCancel = () => {
    setEditingUser(null);
    setEditError(null);
    setEditSuccess(null);
  };
  
  const handleEditSubmit = async (userId: number) => {
    setEditLoading(true);
    setEditError(null);
    setEditSuccess(null);
    
    try {
      // Only send fields that are not empty
      const updateData: {
        name?: string;
        email?: string;
        password?: string;
        role?: string;
      } = {};
      
      if (editFormData.name.trim()) updateData.name = editFormData.name;
      if (editFormData.email.trim()) updateData.email = editFormData.email;
      if (editFormData.password.trim()) updateData.password = editFormData.password;
      if (editFormData.role) updateData.role = editFormData.role;
      
      if (Object.keys(updateData).length === 0) {
        setEditError('No changes to save');
        setEditLoading(false);
        return;
      }
      
      await axios.put(`/api/auth/users/${userId}`, updateData);
      
      // Refresh user list
      await fetchUsers();
      
      setEditSuccess('User updated successfully');
      // Clear password field but keep the other fields
      setEditFormData({
        ...editFormData,
        password: ''
      });
    } catch (err: any) {
      setEditError(err.response?.data?.message || 'Error updating user');
    } finally {
      setEditLoading(false);
    }
  };
  
  const handleDeleteClick = (userId: number) => {
    setDeleteConfirm(userId);
  };
  
  const handleDeleteCancel = () => {
    setDeleteConfirm(null);
  };
  
  const handleDeleteConfirm = async (userId: number) => {
    setDeleteLoading(true);
    
    try {
      await axios.delete(`/api/auth/users/${userId}`);
      
      // Refresh user list
      await fetchUsers();
      
      setDeleteConfirm(null);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error deleting user');
    } finally {
      setDeleteLoading(false);
    }
  };
  
  const filteredUsers = users.filter(user => {
    const searchValue = searchTerm.toLowerCase();
    return (
      user.name.toLowerCase().includes(searchValue) ||
      user.email.toLowerCase().includes(searchValue) ||
      user.role.toLowerCase().includes(searchValue)
    );
  });
  
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };
  
  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>
          <FontAwesomeIcon icon={faUsers} className="me-2" />
          User Management
        </h2>
      </div>
      
      {error && (
        <div className="alert alert-danger mb-4" role="alert">
          {error}
        </div>
      )}
      
      <div className="row mb-4">
        <div className="col-md-6">
          <div className="input-group">
            <span className="input-group-text">
              <FontAwesomeIcon icon={faSearch} />
            </span>
            <input
              type="text"
              className="form-control"
              placeholder="Search users..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
            />
          </div>
        </div>
      </div>
      
      {loading && users.length === 0 ? (
        <div className="text-center py-5">
          <FontAwesomeIcon icon={faSpinner} spin size="3x" className="text-primary mb-3" />
          <h3>Loading users...</h3>
        </div>
      ) : filteredUsers.length === 0 ? (
        <div className="card shadow-sm">
          <div className="card-body text-center p-5">
            <h4 className="mb-3">No users found</h4>
            <p className="text-muted mb-0">Try adjusting your search term.</p>
          </div>
        </div>
      ) : (
        <div className="card shadow-sm">
          <div className="table-responsive">
            <table className="table table-hover mb-0">
              <thead className="bg-light">
                <tr>
                  <th scope="col">Name</th>
                  <th scope="col">Email</th>
                  <th scope="col">Role</th>
                  <th scope="col">Joined</th>
                  <th scope="col" className="text-end">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map((user) => (
                  <React.Fragment key={user.id}>
                    <tr>
                      <td>{user.name}</td>
                      <td>{user.email}</td>
                      <td>
                        <span className={`badge ${user.role === 'admin' ? 'bg-danger' : 'bg-info'}`}>
                          {user.role}
                        </span>
                      </td>
                      <td>{formatDate(user.created_at)}</td>
                      <td className="text-end">
                        {deleteConfirm === user.id ? (
                          <div className="btn-group">
                            <button 
                              className="btn btn-sm btn-danger"
                              onClick={() => handleDeleteConfirm(user.id)}
                              disabled={deleteLoading}
                            >
                              {deleteLoading ? (
                                <FontAwesomeIcon icon={faSpinner} spin />
                              ) : (
                                <>Confirm</>
                              )}
                            </button>
                            <button 
                              className="btn btn-sm btn-outline-secondary"
                              onClick={handleDeleteCancel}
                              disabled={deleteLoading}
                            >
                              <FontAwesomeIcon icon={faTimes} />
                            </button>
                          </div>
                        ) : (
                          <div className="btn-group">
                            <button 
                              className="btn btn-sm btn-outline-primary"
                              onClick={() => handleEditClick(user)}
                            >
                              <FontAwesomeIcon icon={faEdit} />
                            </button>
                            <button 
                              className="btn btn-sm btn-outline-danger"
                              onClick={() => handleDeleteClick(user.id)}
                              disabled={user.id === currentUser?.id}
                              title={user.id === currentUser?.id ? 'Cannot delete your own account' : ''}
                            >
                              <FontAwesomeIcon icon={faTrash} />
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                    
                    {/* Edit form row */}
                    {editingUser === user.id && (
                      <tr>
                        <td colSpan={5} className="bg-light">
                          <div className="p-3">
                            <h5 className="mb-3">Edit User</h5>
                            
                            {editError && (
                              <div className="alert alert-danger" role="alert">
                                {editError}
                              </div>
                            )}
                            
                            {editSuccess && (
                              <div className="alert alert-success" role="alert">
                                <FontAwesomeIcon icon={faCheckCircle} className="me-2" />
                                {editSuccess}
                              </div>
                            )}
                            
                            <div className="row mb-3">
                              <div className="col-md-4">
                                <label htmlFor="name" className="form-label">Name</label>
                                <div className="input-group">
                                  <span className="input-group-text">
                                    <FontAwesomeIcon icon={faUsers} />
                                  </span>
                                  <input
                                    type="text"
                                    className="form-control"
                                    id="name"
                                    name="name"
                                    value={editFormData.name}
                                    onChange={handleEditChange}
                                  />
                                </div>
                              </div>
                              
                              <div className="col-md-4">
                                <label htmlFor="email" className="form-label">Email</label>
                                <div className="input-group">
                                  <span className="input-group-text">
                                    <FontAwesomeIcon icon={faEnvelope} />
                                  </span>
                                  <input
                                    type="email"
                                    className="form-control"
                                    id="email"
                                    name="email"
                                    value={editFormData.email}
                                    onChange={handleEditChange}
                                  />
                                </div>
                              </div>
                              
                              <div className="col-md-4">
                                <label htmlFor="password" className="form-label">New Password</label>
                                <div className="input-group">
                                  <span className="input-group-text">
                                    <FontAwesomeIcon icon={faLock} />
                                  </span>
                                  <input
                                    type="password"
                                    className="form-control"
                                    id="password"
                                    name="password"
                                    value={editFormData.password}
                                    onChange={handleEditChange}
                                    placeholder="Leave blank to keep current"
                                  />
                                </div>
                              </div>
                            </div>

                            <div className="row mb-3">
                              <div className="col-md-4">
                                <label htmlFor="role" className="form-label">Role</label>
                                <div className="input-group">
                                  <span className="input-group-text">
                                    <FontAwesomeIcon icon={faUserShield} />
                                  </span>
                                  <select
                                    className="form-select"
                                    id="role"
                                    name="role"
                                    value={editFormData.role}
                                    onChange={handleEditChange}
                                  >
                                    <option value="user">User</option>
                                    <option value="admin">Admin</option>
                                  </select>
                                </div>
                              </div>
                            </div>
                            
                            <div className="d-flex justify-content-end">
                              <button
                                type="button"
                                className="btn btn-outline-secondary me-2"
                                onClick={handleEditCancel}
                                disabled={editLoading}
                              >
                                <FontAwesomeIcon icon={faTimes} className="me-1" />
                                Cancel
                              </button>
                              <button
                                type="button"
                                className="btn btn-primary"
                                onClick={() => handleEditSubmit(user.id)}
                                disabled={editLoading}
                              >
                                {editLoading ? (
                                  <>
                                    <FontAwesomeIcon icon={faSpinner} spin className="me-1" />
                                    Saving...
                                  </>
                                ) : (
                                  <>
                                    <FontAwesomeIcon icon={faSave} className="me-1" />
                                    Save Changes
                                  </>
                                )}
                              </button>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement; 